import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Book,
  Clock,
  Shield,
  Loader2,
  AlertCircle,
  FileText,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocShareView = () => {
  const { isDarkMode } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [documentation, setDocumentation] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  const fetchDocumentation = useCallback(async (isRefresh = false) => {
    const sessionToken = sessionStorage.getItem('doc_session');
    const docToken = sessionStorage.getItem('doc_token');
    
    if (!sessionToken || !docToken) {
      setError('Accès non autorisé. Veuillez vous authentifier via le lien de partage.');
      setLoading(false);
      return;
    }
    
    if (isRefresh) {
      setRefreshing(true);
    }
    
    try {
      const response = await fetch(`${API_URL}/api/public/doc-content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          token: docToken, 
          session_token: sessionToken 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setDocumentation(data);
        setLastRefresh(new Date());
        setError(null);
        if (isRefresh) {
          toast.success('Documentation mise à jour');
        }
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors du chargement');
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  useEffect(() => {
    fetchDocumentation();
  }, [fetchDocumentation]);
  
  const handleRefresh = () => {
    fetchDocumentation(true);
  };
  
  const formatLastRefresh = () => {
    if (!lastRefresh) return '';
    return lastRefresh.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[hsl(220,17%,10%)]' : 'bg-gradient-to-br from-slate-100 to-indigo-100'}`}>
        <div className="text-center">
          <Loader2 className={`h-12 w-12 animate-spin mx-auto ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
          <p className={`mt-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            Chargement de la documentation...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-[hsl(220,17%,10%)]' : 'bg-gradient-to-br from-slate-100 to-indigo-100'}`}>
        <Card className={`w-full max-w-md ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
          <CardContent className="pt-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className={`text-xl font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Accès refusé
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {error}
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Veuillez utiliser le lien de partage qui vous a été communiqué.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[hsl(220,17%,10%)]' : 'bg-gradient-to-br from-slate-50 to-indigo-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b backdrop-blur-xl ${isDarkMode ? 'bg-[hsl(220,14%,12%)]/90 border-[hsl(220,14%,22%)]' : 'bg-white/80 border-slate-200'}`}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
              <Book className={`h-6 w-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
            </div>
            <div>
              <h1 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {documentation?.title || 'Documentation B2RCS'}
              </h1>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                Document partagé en lecture seule
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className={`${isDarkMode ? 'border-emerald-500/50 text-emerald-400' : 'border-emerald-500 text-emerald-600'}`}>
              <Shield className="h-3 w-3 mr-1" />
              Accès validé
            </Badge>
            {lastRefresh && (
              <span className={`text-xs hidden sm:inline ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Mis à jour à {formatLastRefresh()}
              </span>
            )}
            <Button 
              onClick={handleRefresh} 
              variant="outline" 
              size="sm"
              disabled={refreshing}
              className={isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="ml-2 hidden sm:inline">Actualiser</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {documentation?.content ? (
          <Card className={`${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                  <FileText className="h-5 w-5" />
                  {documentation.title || 'Documentation'}
                </CardTitle>
                {documentation.updated_at && (
                  <Badge variant="secondary" className={isDarkMode ? 'bg-[hsl(220,14%,20%)]' : ''}>
                    <Clock className="h-3 w-3 mr-1" />
                    {new Date(documentation.updated_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div 
                className={`prose max-w-none ${isDarkMode ? 'prose-invert' : ''}`}
                style={{
                  '--tw-prose-headings': isDarkMode ? '#fff' : '#1e293b',
                  '--tw-prose-body': isDarkMode ? '#cbd5e1' : '#475569',
                  '--tw-prose-bold': isDarkMode ? '#fff' : '#1e293b',
                  '--tw-prose-code': isDarkMode ? '#f59e0b' : '#c026d3',
                }}
                dangerouslySetInnerHTML={{ __html: documentation.content }}
              />
            </CardContent>
          </Card>
        ) : (
          <Card className={`${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
            <CardContent className="py-16 text-center">
              <FileText className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                Aucune documentation disponible pour le moment.
              </p>
              <Button 
                onClick={handleRefresh} 
                variant="outline" 
                className="mt-4"
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Réessayer
              </Button>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className={`py-6 text-center text-sm border-t ${isDarkMode ? 'text-slate-500 border-[hsl(220,14%,18%)]' : 'text-slate-400 border-slate-200'}`}>
        <p>Documentation B2RCS • Document partagé en lecture seule</p>
        <p className="text-xs mt-1">Pour toute question, contactez l'administrateur qui vous a partagé ce document.</p>
      </footer>
    </div>
  );
};

export default DocShareView;
